import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { User, Edit3, Users, UserCheck, Camera, Save, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user, checkAuth } = useAuth();
  const [editing, setEditing] = useState(false);
  const [bio, setBio] = useState("");
  const [name, setName] = useState("");
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [activeList, setActiveList] = useState("followers");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user) {
      setBio(user.bio || "");
      setName(user.name || "");
      loadFollowers();
      loadFollowing();
    }
  }, [user]);

  const loadFollowers = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/profile/${user.id}/followers`);
      setFollowers(res.data.followers);
    } catch (err) {
      console.error("[profile] loadFollowers failed:", err?.response?.status, err?.message);
      setFollowers([]);
    }
  };

  const loadFollowing = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/profile/${user.id}/following`);
      setFollowing(res.data.following);
    } catch (err) {
      console.error("[profile] loadFollowing failed:", err?.response?.status, err?.message);
      setFollowing([]);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await axios.put(`${API}/profile`, { name: name.trim(), bio: bio.trim() });
      await checkAuth();
      setEditing(false);
    } catch (err) {
      console.error("[profile] save failed:", err?.response?.data?.detail || err?.message);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/profile/avatar`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await checkAuth();
    } catch (err) {
      console.error("[profile] avatar upload failed:", err?.response?.data?.detail || err?.message);
    }
    setUploading(false);
  };

  if (!user) return null;

  const profilePicUrl = user.profile_picture
    ? `${process.env.REACT_APP_BACKEND_URL}${user.profile_picture}`
    : null;

  return (
    <div data-testid="profile-page">
      {/* Profile Header */}
      <div className="bg-white border border-[#E5E2D9] p-6 mb-6">
        <div className="flex items-start gap-6">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <div className="w-20 h-20 rounded-full bg-[#F4F1E6] border-2 border-[#E5E2D9] flex items-center justify-center overflow-hidden">
              {profilePicUrl ? (
                <img src={profilePicUrl} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <User size={32} className="text-[#718096]" />
              )}
            </div>
            <label className="absolute -bottom-1 -right-1 w-7 h-7 bg-[#1A2B3C] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#2A405A] transition-colors" data-testid="avatar-upload-btn">
              <Camera size={12} className="text-white" />
              <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} disabled={uploading} />
            </label>
          </div>

          {/* Info */}
          <div className="flex-1">
            {editing ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E5E2D9] text-sm focus:ring-2 focus:ring-[#1A2B3C] focus:border-transparent outline-none"
                  placeholder="Your name"
                  data-testid="edit-name-input"
                />
                <textarea
                  value={bio}
                  onChange={e => setBio(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-[#E5E2D9] text-sm focus:ring-2 focus:ring-[#1A2B3C] focus:border-transparent outline-none resize-none"
                  placeholder="Write a short bio..."
                  maxLength={500}
                  data-testid="edit-bio-input"
                />
                <div className="flex gap-2">
                  <button className="btn-primary text-xs" onClick={handleSaveProfile} data-testid="save-profile-btn">
                    <Save size={12} /> Save
                  </button>
                  <button className="btn-ghost text-xs" onClick={() => { setEditing(false); setBio(user.bio || ""); setName(user.name || ""); }} data-testid="cancel-edit-btn">
                    <X size={12} /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="font-serif text-xl font-medium text-[#1A1A1A]" data-testid="profile-name">{user.name}</h2>
                  <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#F4F1E6] border border-[#E5E2D9] text-[#718096]">
                    {user.role === "echo_maker" ? "Echoe Maker" : "Echoe Hunter"}
                  </span>
                </div>
                <p className="text-sm text-[#4A5568] mb-3" data-testid="profile-bio">
                  {user.bio || "No bio yet."}
                </p>
                <button className="btn-ghost text-xs" onClick={() => setEditing(true)} data-testid="edit-profile-btn">
                  <Edit3 size={12} /> Edit Profile
                </button>
              </>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-6 mt-5 pt-4 border-t border-[#E5E2D9]">
          <button
            className={`text-center ${activeList === "followers" ? "text-[#1A2B3C]" : "text-[#718096]"}`}
            onClick={() => setActiveList("followers")}
            data-testid="show-followers-btn"
          >
            <span className="block font-serif text-lg font-medium">{user.followers_count}</span>
            <span className="font-mono text-xs uppercase tracking-wider">Followers</span>
          </button>
          <button
            className={`text-center ${activeList === "following" ? "text-[#1A2B3C]" : "text-[#718096]"}`}
            onClick={() => setActiveList("following")}
            data-testid="show-following-btn"
          >
            <span className="block font-serif text-lg font-medium">{user.following_count}</span>
            <span className="font-mono text-xs uppercase tracking-wider">Following</span>
          </button>
        </div>
      </div>

      {/* Followers / Following List */}
      <div className="bg-white border border-[#E5E2D9]">
        <div className="px-4 py-3 border-b border-[#E5E2D9] bg-[#F9F7F1]">
          <span className="font-mono text-xs uppercase tracking-widest text-[#718096]">
            {activeList === "followers" ? "Followers" : "Following"}
          </span>
        </div>
        <div className="divide-y divide-[#E5E2D9]">
          {(activeList === "followers" ? followers : following).length === 0 ? (
            <div className="p-6 text-center text-sm text-[#718096]" data-testid="empty-list">
              {activeList === "followers" ? "No followers yet" : "Not following anyone yet"}
            </div>
          ) : (
            (activeList === "followers" ? followers : following).map(u => (
              <div key={u.id} className="flex items-center justify-between px-4 py-3" data-testid={`user-list-item-${u.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#F4F1E6] border border-[#E5E2D9] flex items-center justify-center">
                    <User size={14} className="text-[#718096]" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-[#1A1A1A]">{u.name}</span>
                    <span className="ml-2 font-mono text-[10px] uppercase tracking-wider text-[#718096]">
                      {u.role === "echo_maker" ? "Echoe Maker" : "Echoe Hunter"}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
